
import Vue, { PropType } from 'vue';
import { CvGrid, CvButton, CvRow, CvColumn, CvButtonSet } from '@carbon/vue';
import GeneratePdf16 from '@carbon/icons-vue/es/generate-pdf/16';
import { mapActions, mapState } from 'pinia';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import Close32 from '@carbon/icons-vue/es/close/16';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  trySavePersonalization,
  generateProofPreview,
  getSmallDetailsFiles,
  triggerSmallDetailsCheck,
  isRevalidationNeeded,
} from '@/utils/personalizationHelperV2';
import { PdpChoicesSetterType } from '@/setters/typings';
import { revalidateSmallDetails } from '@/utils/smallDetailsHelperV2';
import { getIntershopRedirectionUrl } from '@/utils/index';

declare module 'vue/types/vue' {
  interface Vue {
    $userLoginOptions: { data: string; iv: string };
    $context: string;
    $isCommunicationSentToCC: boolean;
    $notifyCustomerOnFirstProof: boolean;
    $customerCareEmail: string;
  }
}

export default Vue.extend({
  name: 'PreviewPdf',

  inject: [
    '$userLoginOptions',
    '$context',
    '$isCommunicationSentToCC',
    '$notifyCustomerOnFirstProof',
    '$customerCareEmail',
  ],

  components: {
    CvButton,
    CvRow,
    CvColumn,
    GeneratePdf16,
    CvButtonSet,
    CvGrid,
    Close32,
  },
  props: {
    pdpChoices: {
      type: Object as PropType<PdpChoicesSetterType>,
      required: false,
      default: {} as PdpChoicesSetterType,
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOrderDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      pdf: '',
      logoImgSrc: require('../../../../../assets/pdfLogo.png'),
      pdfImg: '',
      getIntershopRedirectionUrl,
    };
  },
  computed: {
    ...mapState(useShapeStore, ['shapes', 'personalization']),
    ...mapState(useProductStore, ['data', 'unavailablePrintPositions']),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useGlobalStore, ['isLoaderActive', 'isProcessingOrder']),

    isEditMode(): boolean {
      return this.personalizationId !== '';
    },
  },
  mounted() {
    this.showModal();
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    ...mapActions(useProductStore, ['setSelectedVariant']),
    ...mapActions(useShapeStore, ['getPersonalization', 'recreateTextPdfs']),

    ...mapActions(useNotificationStore, ['runValidators']),

    async handleOnToShoppingCard() {
      if (DoSmallDetailsPart && triggerSmallDetailsCheck()) {
        this.$emit('action:trigger-small-details');
      } else {
        try {
          this.setIsProcessingOrder(true);
          this.setIsLoaderActive(true);
          await this.recreateTextPdfs();

          const personalizationId = await trySavePersonalization(
            this.isEditMode,
            this.pdpChoices.productVariants,
            this.sapCustomerId,
            false,
            this.isDirectApproval,
            this.customerCompanyLogoUrl,
            this.languageInUse,
            this.$isCommunicationSentToCC,
            this.$notifyCustomerOnFirstProof,
            this.$customerCareEmail
          );

          if (this.$context === 'CustomerCare') {
            const url = this.getIntershopRedirectionUrl(
              personalizationId,
              false,
              this.$userLoginOptions.data,
              this.$userLoginOptions.iv
            );
            window.open(url, '_blank');
          }

          this.$emit('wizard:ordered', { personalizationId });
          this.$emit('action:disable-pintura');
        } catch (e: unknown) {
          console.log((e as Error).message);
        } finally {
          this.setIsProcessingOrder(false);
          this.setIsLoaderActive(false);
        }
      }
    },

    async showModal() {
      if (this.isEditMode) {
        this.getPersonalization(this.personalizationId);
      }
      this.setIsLoaderActive(true);

      if (DoSmallDetailsPart && isRevalidationNeeded()) {
        await revalidateSmallDetails();

        this.runValidators();
      }

      const smallDetailsFiles = DoSmallDetailsPart
        ? getSmallDetailsFiles(this.shapes)
        : [];

      const proofPreview = await generateProofPreview(
        smallDetailsFiles,
        this.sapCustomerId,
        this.customerCompanyLogoUrl,
        this.languageInUse,
        this.pdpChoices
      );

      this.pdf = proofPreview.pdfUrl;
      this.pdfImg = proofPreview.pdfPreviewUrl;
      this.setIsLoaderActive(false);
      this.$modal.show('pdf-preview-modal');
    },
    onCloseModal() {
      this.pdf = '';
      this.pdfImg = '';
      this.$emit('close:preview-pdf');
    },
    async onPreviewPdf(downloadPdfPreviewUrl: string) {
      // in case of usage proof approval component from Backend portal do not use
      // vue pdf app for pdf preview, open pdf in a new tab in browser pdf viewer

      try {
        window.open(downloadPdfPreviewUrl, '_blank');
      } catch (e) {
        console.log(e);
      }
    },
  },
});
